import { Link } from "react-router-dom";
import { useRecentNotes } from "./hooks";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const LIMIT = 10;

interface RecentNotesPanelProps {
  /** When provided, wraps the panel in a Sheet slide-over. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function RecentNotesPanel({ open, onOpenChange }: RecentNotesPanelProps = {}) {
  const { data, isLoading } = useRecentNotes({ limit: LIMIT });

  const content = (
    <div className="flex flex-col gap-2 overflow-y-auto">
      {isLoading ? (
        <>
          <Skeleton className="h-12 w-full" data-testid="skeleton" />
          <Skeleton className="h-12 w-full" data-testid="skeleton" />
        </>
      ) : data?.items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No notes yet. Add one from any cluster or analysis card.
        </p>
      ) : (
        data?.items.map((n) => (
          <div
            key={n.id}
            className="rounded-md border border-border bg-background/50 p-2 text-xs"
          >
            <div className="mb-1 text-[10px] uppercase text-muted-foreground">
              {n.target_kind ?? "free-form"}
              {n.target_id ? ` · ${n.target_id}` : ""}
            </div>
            <div className="line-clamp-2 text-foreground">{n.body}</div>
          </div>
        ))
      )}
    </div>
  );

  // Sheet variant — used by the dashboard toolbar toggle
  if (open !== undefined && onOpenChange !== undefined) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[320px] sm:w-[360px]">
          <SheetHeader>
            <SheetTitle className="text-base">
              Recent notes
            </SheetTitle>
            <Link to="/notes" className="text-xs text-primary hover:underline">
              See all →
            </Link>
          </SheetHeader>
          <div className="mt-4">{content}</div>
        </SheetContent>
      </Sheet>
    );
  }

  // Inline aside variant — for use in layouts that have a right-column slot
  return (
    <aside className="flex h-full w-full flex-col border-l border-border bg-background/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
          Recent notes
        </h3>
        <Link to="/notes" className="text-xs text-primary hover:underline">
          See all →
        </Link>
      </div>
      {content}
    </aside>
  );
}
